import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import type { RecentlyUsedRow } from "../types/PluginTypes"

ModuleRegistry.registerModules([AllCommunityModule]);

type Props = {
  rows: RecentlyUsedRow[];
  onSelectionChange?: (selectedRow: RecentlyUsedRow | null) => void;
  onClear?: () => void;
  onDeleteRow?: (rowKey: string) => void;
};

function DeleteAllHeader(props: { onClear?: () => void; hasRows: boolean }) {
  return (
    <button
      className="recently-used-clear-button"
      type="button"
      onClick={() => props.onClear?.()}
      disabled={!props.hasRows}
      title="Remove all recently used materials"
    >
      ✕ All
    </button>
  );
}

function DeleteCellRenderer(props: { data: RecentlyUsedRow; context: { onDeleteRow?: (rowKey: string) => void } }) {
  return (
    <button
      className="recently-used-delete-button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        props.context.onDeleteRow?.(props.data.rowKey);
      }}
      title="Remove from recently used"
    >
      <span className="recently-used-delete-icon" aria-hidden="true">
        delete
      </span>
    </button>
  );
}

export default function RecentlyUsedTable({ rows, onSelectionChange, onClear, onDeleteRow }: Props) {
  const columnDefs = useMemo<ColDef<RecentlyUsedRow>[]>(
    () => [
      // { headerName: "bmId", field: "bmId", flex: 1, filter: false },
      { headerName: "Material", field: "bmName", flex: 1, filter: false },
      { headerName: "Density", field: "bmDensity", flex: 1, filter: false },
      // { headerName: "stId", field: "stMaterialId", flex: 1, filter: false },
      { headerName: "Surface Treatment", field: "stName", flex: 1, filter: false },
      // { headerName: "ST Density", field: "stDensity", flex: 1, filter: false },
      { headerName: "Coverage", field: "stCoverage", flex: 1, filter: false },
      { headerName: "Thickness", field: "stThickness", flex: 1, filter: false },
      {
        headerComponent: DeleteAllHeader,
        headerComponentParams: { onClear, hasRows: rows.length > 0 },
        field: "rowKey",
        width: 72,
        maxWidth: 72,
        resizable: false,
        sortable: false,
        cellRenderer: DeleteCellRenderer,
        cellStyle: { padding: 0, display: "flex", alignItems: "center", justifyContent: "center" },
      },
    ],
    [onClear, rows.length]
  );

  const rowSelection = useMemo(
    () => ({
      mode: "singleRow" as const,
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: true,
    }),
    []
  );

  const headerHeight = 47;
  const rowHeight = 42;
  const maxHeight = 300;
  const emptyStateHeight = 75;

  const calculatedHeight = headerHeight + rows.length * rowHeight;

  const tableHeight =
    rows.length === 0
      ? emptyStateHeight
      : Math.min(maxHeight, calculatedHeight);

  return (
    <div
      className="ag-theme-alpine"
      style={{ width: "100%", height: `${tableHeight}px` }}
    >
      <AgGridReact<RecentlyUsedRow>
        rowData={rows}
        columnDefs={columnDefs}
        rowSelection={rowSelection}
        getRowId={(params) => params.data.rowKey}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        context={{ onDeleteRow }}
        onSelectionChanged={(event) => {
          const selected = event.api.getSelectedRows()[0] ?? null;
          onSelectionChange?.(selected);
        }}
      />
    </div>
  );
}